import React from "react";
import { nextId } from "./idGenerator";

export interface FormFieldProps {
  label?: React.ReactNode;
  required?: boolean;
  error?: React.ReactNode;
  hint?: React.ReactNode;
  htmlFor?: string;
  className?: string;
  children: React.ReactElement;
}

type ChildProps = {
  id?: string;
  required?: boolean;
  "aria-invalid"?: boolean | "true" | "false";
  "aria-describedby"?: string;
};

function FormField({
  label,
  required = false,
  error,
  hint,
  htmlFor,
  className,
  children,
}: FormFieldProps) {
  const [generatedFieldId] = React.useState(() => nextId("ui-field"));
  const fieldId = htmlFor || generatedFieldId;
  const errorId = `${fieldId}-error`;
  const hintId = `${fieldId}-hint`;

  const child = React.Children.only(children) as React.ReactElement<ChildProps>;

  const describedByIds = [
    child.props["aria-describedby"] || null,
    hint ? hintId : null,
    error ? errorId : null,
  ]
    .filter(Boolean)
    .join(" ");

  const enhancedChild = React.cloneElement(child, {
    id: child.props.id || fieldId,
    "aria-invalid": error ? "true" : child.props["aria-invalid"],
    "aria-describedby": describedByIds || undefined,
    required: child.props.required || required || undefined,
  });

  const classes = ["ui-form-field", error ? "ui-form-field--has-error" : null, className]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classes}>
      {label ? (
        <label className="ui-form-field__label" htmlFor={fieldId}>
          {label}
          {required ? (
            <span className="ui-form-field__required" aria-hidden="true">
              {" "}
              *
            </span>
          ) : null}
        </label>
      ) : null}
      <div className="ui-form-field__control">{enhancedChild}</div>
      {hint ? (
        <p className="ui-form-field__hint" id={hintId}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <span className="ui-form-field__error" id={errorId} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

export default FormField;
